/**
 * Servicio unificado de notificaciones para SICECOM ERP
 * Cumple con las especificaciones de SYSTEM.md sección 14
 */

class NotificationService {
  constructor() {
    this.isInitialized = false
    this.init()
  }

  init() {
    if (this.isInitialized) return

    // Esperar a que el componente esté disponible
    if (typeof window !== 'undefined') {
      this.waitForNotificationSystem()
    }

    this.isInitialized = true
  }

  waitForNotificationSystem() {
    const checkInterval = setInterval(() => {
      if (window.$notification) {
        clearInterval(checkInterval)
        this.notificationSystem = window.$notification
      }
    }, 100)

    // Timeout después de 5 segundos
    setTimeout(() => {
      clearInterval(checkInterval)
      if (!this.notificationSystem) {
        console.warn('[NotificationService] Sistema de notificaciones no disponible después de 5 segundos')
      }
    }, 5000)
  }

  /**
   * Muestra una notificación de éxito
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  success(message, options = {}) {
    this.logNotification('success', message)
    if (this.notificationSystem) {
      return this.notificationSystem.success(message, options)
    }
  }

  /**
   * Muestra una notificación de error
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  error(message, options = {}) {
    this.logNotification('error', message)
    if (this.notificationSystem) {
      return this.notificationSystem.error(message, options)
    }
    console.error(`[ERROR] ${message}`)
  }

  /**
   * Muestra una notificación de advertencia
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  warning(message, options = {}) {
    this.logNotification('warning', message)
    if (this.notificationSystem) {
      return this.notificationSystem.warning(message, options)
    }
    console.warn(`[WARNING] ${message}`)
  }

  /**
   * Muestra una notificación informativa
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  info(message, options = {}) {
    this.logNotification('info', message)
    if (this.notificationSystem) {
      return this.notificationSystem.info(message, options)
    }
    console.info(`[INFO] ${message}`)
  }

  /**
   * Cierra una notificación específica
   * @param {number} id - ID de la notificación
   */
  close(id) {
    if (this.notificationSystem) {
      this.notificationSystem.close(id)
    }
  }

  /**
   * Limpia todas las notificaciones
   */
  clear() {
    if (this.notificationSystem) {
      this.notificationSystem.clear()
    }
  }

  /**
   * Registra la notificación en el log para auditoría
   * @param {string} type - Tipo de notificación
   * @param {string} message - Mensaje
   */
  logNotification() {
    // Auditoría pendiente: enviar al backend cuando se implemente el endpoint
  }

  /**
   * Obtiene la empresa actual del contexto
   */
  getCurrentEmpresa() {
    // Para client: del subdominio o store
    const hostname = window.location.hostname
    const subdomain = hostname.split('.')[0]
    return subdomain !== 'localhost' && subdomain !== '127' ? subdomain : 'demo'
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUsuario() {
    // Lógica para obtener usuario actual del store
    return localStorage.getItem('client_usuario_id') || 'desconocido'
  }

  /**
   * Método para futuras integraciones con backend
   */
  // eslint-disable-next-line no-unused-vars
  sendToAuditLog(type, message, empresa, usuario) {
    // TODO: Implementar envío al backend para registro en auditoría
    // Ejemplo:
    // api.post('/auditoria/notificaciones', {
    //   tipo: type,
    //   mensaje: message,
    //   empresa_id: empresa,
    //   usuario_id: usuario,
    //   timestamp: new Date().toISOString(),
    //   ip: obtenerIP(),
    //   modulo: 'notificaciones'
    // })
  }
}

// Crear instancia singleton
const notificationService = new NotificationService()

export default notificationService

// Exportar funciones convenientes para uso directo
export const notifySuccess = (message, options) => notificationService.success(message, options)
export const notifyError = (message, options) => notificationService.error(message, options)
export const notifyWarning = (message, options) => notificationService.warning(message, options)
export const notifyInfo = (message, options) => notificationService.info(message, options)